import type {
  AppelsCapabilities,
  AppelsSettings,
  AppelsStats,
  Call,
  CallOutcome,
  CallRequest,
  CallRequestStatus,
  CallStatus,
  MonitoringTokenStatus,
} from "@shared/modules/x-appels";
import { api } from "../../../core/lib/api";
import { moduleEnabled } from "../../../core/blueprint";

// Fonctions typées par route de /api/appels. Les formes d'entrée sont redites
// ici (zod n'est pas une dépendance du web).

const BASE = "/api/appels";

const json = (method: "POST" | "PATCH" | "PUT", body: unknown): RequestInit => ({
  method,
  body: JSON.stringify(body),
});

function qs(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") search.set(k, String(v));
  }
  const s = search.toString();
  return s ? `?${s}` : "";
}

export const getSettings = () => api<AppelsSettings>(`${BASE}/settings`);
export const getCapabilities = () => api<AppelsCapabilities>(`${BASE}/capabilities`);
export const updateSettings = (input: Partial<AppelsSettings>) =>
  api<AppelsSettings>(`${BASE}/settings`, json("PUT", input));
export const getStats = () => api<AppelsStats>(`${BASE}/stats`);

export interface CallsQuery {
  status?: CallStatus[];
  outcome?: CallOutcome[];
  q?: string;
  limit?: number;
}

export const listCalls = (query: CallsQuery = {}) =>
  api<Call[]>(
    `${BASE}/calls${qs({
      status: query.status?.join(","),
      outcome: query.outcome?.join(","),
      q: query.q,
      limit: query.limit,
    })}`,
  );
export const getCall = (id: string) => api<Call>(`${BASE}/calls/${id}`);

export interface CallUpdate {
  outcome?: CallOutcome;
  notes?: string | null;
  handled?: boolean;
}
export const updateCall = (id: string, input: CallUpdate) =>
  api<Call>(`${BASE}/calls/${id}`, json("PATCH", input));

// ---- Demandes transmises par Léo (rappel, annulation) ----

export interface RequestsQuery {
  status?: CallRequestStatus;
  clientId?: string;
  appointmentId?: string;
  callId?: string;
  limit?: number;
}
export const listRequests = (query: RequestsQuery = {}) =>
  api<CallRequest[]>(`${BASE}/requests${qs({ ...query })}`);
export const getRequestsSummary = () =>
  api<{ pending: number; smsFailed: number }>(`${BASE}/requests/summary`);
export const updateRequest = (id: string, input: { status: CallRequestStatus }) =>
  api<CallRequest>(`${BASE}/requests/${id}`, json("PATCH", input));
export const resendRequestSms = (id: string) =>
  api<CallRequest>(`${BASE}/requests/${id}/resend`, { method: "POST" });

/** Jeton de supervision (admin) : état, génération (clair renvoyé une seule fois), révocation. */
export const getMonitoringToken = () => api<MonitoringTokenStatus>(`${BASE}/monitoring/token`);
export const issueMonitoringToken = () =>
  api<MonitoringTokenStatus & { token: string }>(`${BASE}/monitoring/token`, { method: "POST" });
export const revokeMonitoringToken = () =>
  api<void>(`${BASE}/monitoring/token`, { method: "DELETE" });

export const listScenarios = () => api<Array<{ key: string; label: string }>>(`${BASE}/scenarios`);
export const simulateCall = (scenario?: string) =>
  api<{ id: string }>(`${BASE}/simulate`, json("POST", scenario ? { scenario } : {}));

/** L'atelier est un autre module : on ne fait qu'y pointer (fiches client et travail). */
export const atelierEnabled = moduleEnabled("x-atelier");
